import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import type { ElementDeTableau } from './moteur';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LE SEUL FICHIER DE `web/` QUI IMPORTE `socket.io-client` — D-002.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * La décision vaut des deux côtés : « le transport temps réel est isolé dès la V1 derrière
 * `connect`, `publishUpdate`, `onRemoteUpdate`, `publishPointer`, `onRemotePointer`,
 * `disconnect`, **sans aucun appel direct à la bibliothèque ailleurs** ». Le gateway est
 * l'adaptateur côté serveur ; celui-ci l'est côté front, et `e2e/port-du-canal.spec.ts`
 * vérifie par balayage qu'il reste le seul.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **AUCUN JETON NE PASSE PAR ICI, ET C'EST LA BASCULE EN COOKIE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `handshake.auth` ne porte que **l'identifiant du cours**, qui n'est pas un secret et qui
 * ne vaut accès à rien tant que le serveur n'a pas vérifié la participation. Le jeton de
 * session est `httpOnly` : **ce code ne peut pas le lire**, et le navigateur l'attache tout
 * seul au handshake. R1 de `secure-realtime-sockets` en sort renforcée — un cookie de
 * handshake ne s'écrit dans aucun journal de proxy, contrairement à une chaîne de requête.
 *
 * **`withCredentials` est obligatoire** : sans lui, le navigateur n'attache pas les
 * justificatifs vers une autre origine, et `app.swype.fr` appelant `api.swype.fr` en est
 * une. Le symptôme serait un handshake refusé sur une personne connectée.
 */

/** Ce que l'écran du tableau lit du canal. */
export type EtatDuCanal = {
  /** Vrai tant que le produit voit sa propre liaison. */
  readonly relie: boolean;
  /**
   * **Depuis quand le produit ne voit plus la liaison**, en secondes, ou vide.
   *
   * F-30 : « **pendant la coupure, le produit dit à la personne qu'elle est coupée et la
   * laisse continuer à tracer**, en la prévenant que ce qu'elle trace peut ne pas être
   * retenu. **L'asymétrie avec la règle qui cache la liaison des autres est voulue** :
   * sur sa propre coupure une personne peut agir. »
   */
  readonly coupeDepuisSecondes: number | null;
  /** Les curseurs des autres, par personne. Rien ne s'en conserve. */
  readonly curseurs: ReadonlyMap<string, CurseurDistant>;
  /**
   * **Ce que les autres ont posé depuis l'ouverture, en forme OPAQUE.**
   *
   * D-012 : le moteur de tableau est isolé derrière une interface étroite, donc ce canal
   * **transporte** les éléments et ne les lit pas — exactement ce que le contrat
   * d'écriture dit du serveur, « le produit la transporte et ne l'interprète pas ». Le
   * jour où Ziteboard remplace le moteur, **ce fichier ne bouge pas.**
   */
  readonly recus: readonly ElementDeTableau[];
  /** Les éléments tenus, par élément, avec le prénom de qui les tient. */
  readonly reservations: ReadonlyMap<string, string | null>;
  /**
   * **Le tableau vivant du cours, tel que LE SERVEUR le désigne — D-110.**
   *
   * Il arrive par le salon, à l'admission, et **c'est la seule source qui le dise sans rien
   * créer**. `POST /cours/:id/tableaux` en ouvre un neuf à chaque appel : s'en servir pour
   * « savoir où écrire » figeait le tableau des autres à chaque arrivée.
   *
   * `null` sur une liaison de présence seule, et sur un cours qui n'a **encore aucun**
   * tableau — c'est ce second cas, et lui seul, qui justifie d'en ouvrir un.
   */
  readonly tableauCourantId: string | null;
  /** Son rang, par lequel `relireUneScene` s'adresse. `null` avec l'identifiant. */
  readonly tableauCourantRang: number | null;
  /** Vrai dès que le salon a répondu : avant, on ne sait rien, on ne décide rien. */
  readonly salonRecu: boolean;
  /** Vrai quand le serveur a refusé la version d'un retour de coupure. */
  readonly versionRefusee: boolean;
  /**
   * **L'activité du cours, telle que le serveur l'annonce — F-18, D-120.**
   *
   * `null` tant que rien n'a été annoncé : **une absence et un refus se lisent pareil**,
   * donc l'écran ne montre aucune fenêtre tant qu'il ne sait rien, plutôt que d'en
   * déduire une. Le salon la porte dès le handshake, précisément pour que ce `null` ne
   * dure pas — « un poste qui arrive en cours de silence ne saurait rien » (D-118).
   */
  readonly activite: ActiviteDuCours | null;
  /**
   * ═══════════════════════════════════════════════════════════════════════
   * **F-20 CRITÈRE 4 — UNE STRUCTURE REGARDE-T-ELLE CE COURS EN CE MOMENT ?**
   * ═══════════════════════════════════════════════════════════════════════
   *
   * « **Le passage d'un `orgadmin` est signalé par un bandeau**, qui **ne le nomme
   * pas** et **ne dit pas combien de personnes regardent** » (Q213, Q226, Q426, Q429).
   * « **Celui du compte qui voit tout n'est pas signalé** » (Q214) — invisible sur le
   * moment, constatable après coup par sa trace.
   *
   * **Un booléen, et il n'y a nulle part où un nom ou un nombre pourrait se glisser.**
   * C'est la borne du type, pas une discipline d'écran : ce que ce champ ne porte pas
   * ne peut pas être affiché.
   */
  readonly observee: boolean;
};

/**
 * **Ce que le canal annonce de l'activité d'un cours — F-18.**
 *
 * **Un instant, jamais un compte à rebours**, et c'est ce qui fait que l'écran et le
 * serveur mesurent la même chose. L'échéance se calcule ici, depuis cette valeur et les
 * deux délais qui l'accompagnent ; une annonce répétée du même instant ne rallonge donc
 * rien.
 */
export type ActiviteDuCours = {
  readonly derniereActiviteA: string;
  readonly fenetreOuverte: boolean;
  readonly fenetreSecondes: number;
  readonly reponseSecondes: number;
};

/**
 * **Vrai quand cette charge est une annonce d'activité, vérifiée champ par champ.**
 *
 * Même frontière que `estUnCurseur`, et pour la raison que D-110 a payée : « les charges
 * de socket ne sont pas dans le contrat OpenAPI, donc le client généré ne les type pas, et
 * un `as` est une **affirmation** sur une donnée venue du réseau ». Ici l'affirmation
 * fausse aurait un coût précis — un `NaN` sur `Date.parse` rendrait une fenêtre dont
 * l'échéance ne tombe jamais, ou tombe tout de suite.
 */
export function estUneActivite(charge: unknown): charge is ActiviteDuCours {
  if (typeof charge !== 'object' || charge === null) return false;
  const a = charge as Record<string, unknown>;
  return (
    typeof a.derniereActiviteA === 'string' &&
    !Number.isNaN(Date.parse(a.derniereActiviteA)) &&
    typeof a.fenetreOuverte === 'boolean' &&
    typeof a.fenetreSecondes === 'number' &&
    typeof a.reponseSecondes === 'number'
  );
}

/**
 * **LE CHAMP S'APPELLE `auteurId`, ET IL S'APPELAIT `personneId` — corrigé le 20260826.**
 *
 * Le gateway émet `auteurId` sur `remotePointer`, **comme sur `remoteUpdate`** : il est
 * cohérent des deux côtés. C'était ce type-ci qui divergeait, et le coût de l'écart n'était
 * pas un champ vide mais **une exception à chaque mouvement de souris d'un autre poste** —
 * `claireurDuCurseur(undefined)` itère sur son argument, donc `e is not iterable`, levée
 * pendant le rendu du canevas. **Le tracé de l'autre ne s'affichait pas parce que la surface
 * cessait de se rendre**, et non parce que rien n'arrivait.
 *
 * **Aucun contrôle ne pouvait l'attraper** : les charges de socket ne sont pas dans le
 * contrat OpenAPI, donc le client généré ne les type pas, et `charge as CurseurDistant` est
 * une **affirmation** sur une donnée venue du réseau. Le compilateur l'a crue.
 */
export type CurseurDistant = {
  readonly auteurId: string;
  readonly prenom: string | null;
  readonly x: number;
  readonly y: number;
  readonly trace: boolean;
};

/**
 * **Vrai quand cette charge de socket est un curseur, vérifié champ par champ.**
 *
 * **Ce n'est pas de la défiance envers le serveur, c'est la seule frontière typée qui
 * manquait.** Un `as` sur une charge de socket ne vérifie rien à l'exécution : c'est
 * exactement ce qui a laissé passer le renommage. Une charge malformée est **ignorée** —
 * un curseur perdu ne se voit pas, une exception pendant le rendu arrête tout.
 */
function estUnCurseur(charge: unknown): charge is CurseurDistant {
  if (typeof charge !== 'object' || charge === null) return false;
  const c = charge as Record<string, unknown>;
  return (
    typeof c.auteurId === 'string' &&
    c.auteurId.length > 0 &&
    typeof c.x === 'number' &&
    typeof c.y === 'number' &&
    (typeof c.prenom === 'string' || c.prenom === null) &&
    typeof c.trace === 'boolean'
  );
}

const ETAT_INITIAL: EtatDuCanal = {
  relie: false,
  coupeDepuisSecondes: null,
  curseurs: new Map(),
  recus: [],
  reservations: new Map(),
  tableauCourantId: null,
  tableauCourantRang: null,
  salonRecu: false,
  versionRefusee: false,
  activite: null,
  // **Faux tant que rien n'a été annoncé** : un bandeau supposé serait un bandeau
  // faux, et « le produit refuse partout l'information qui n'ouvre aucune action ».
  observee: false,
};

const ADRESSE_API: string =
  (import.meta.env.VITE_API_URL) ??
  'http://127.0.0.1:3000';

/**
 * **Ouvre le canal d'un cours, et rend ce que l'écran doit peindre.**
 *
 * **Les six opérations de D-002 sont là, et la septième de D-120 avec elles** : `connect`
 * est l'effet d'ouverture, `disconnect` son nettoyage, `publishUpdate` et `publishPointer`
 * sont les deux fonctions rendues, `onRemoteUpdate` et `onRemotePointer` sont deux des
 * écoutes posées ci-dessous, et `publishActivity` en est une troisième — l'annonce de la
 * fenêtre d'inactivité de F-18, qui ne part que du serveur.
 */
export function useCanalDuCours(coursId: string | null) {
  const [etat, poserLEtat] = useState<EtatDuCanal>(ETAT_INITIAL);
  const liaison = useRef<Socket | null>(null);

  useEffect(() => {
    if (coursId === null) return;

    // ── `connect` ──
    const socket = io(`${ADRESSE_API}/cours`, {
      auth: { coursId },
      // **Le cookie de session part avec**, et il ne peut pas partir autrement : il est
      // `httpOnly`, donc invisible à ce code.
      withCredentials: true,
      reconnection: true,
      reconnectionDelayMax: 5_000,
    });
    liaison.current = socket;

    socket.on('connect', () => {
      poserLEtat((precedent) => ({
        ...precedent,
        relie: true,
        coupeDepuisSecondes: null,
      }));
    });

    // **La coupure ne fait rien pendant trente secondes**, F-30 : « en deçà, rien ne se
    // passe ». Ce que l'écran montre est donc le compteur, pas une sortie — c'est le
    // serveur qui décide de sortir quelqu'un, et lui seul.
    let coupeeA: number | null = null;
    const battement = setInterval(() => {
      if (coupeeA === null) return;
      poserLEtat((precedent) => ({
        ...precedent,
        coupeDepuisSecondes: Math.round((Date.now() - coupeeA!) / 1000),
      }));
    }, 1_000);

    socket.on('disconnect', () => {
      coupeeA = Date.now();
      poserLEtat((precedent) => ({
        ...precedent,
        relie: false,
        coupeDepuisSecondes: 0,
        // **Les curseurs des autres disparaissent** : le produit ne les voit plus, et
        // les laisser figés à l'écran ferait croire que quelqu'un est immobile.
        curseurs: new Map(),
      }));
    });
    socket.io.on('reconnect', () => {
      coupeeA = null;
    });

    socket.on('salon', (charge: unknown) => {
      const salon = charge as {
        tableauCourantId?: unknown;
        tableauCourantRang?: unknown;
        activite?: unknown;
        reservations?: { elementId: string; prenom: string | null }[];
      };
      poserLEtat((precedent) => ({
        ...precedent,
        // **L'arrivant apprend la fenêtre au handshake, et pas à la prochaine
        // annonce** : celle-ci ne part qu'au changement d'instant, donc sans ce champ
        // il aurait pu attendre quinze minutes pour savoir qu'une fenêtre est posée.
        activite: estUneActivite(salon.activite) ? salon.activite : precedent.activite,
        // **Le salon portait déjà ces deux champs et ce handler les jetait** — D-110. Le
        // premier existait depuis le LOT-4a, le second est ajouté avec ce correctif.
        tableauCourantId:
          typeof salon.tableauCourantId === 'string' ? salon.tableauCourantId : null,
        tableauCourantRang:
          typeof salon.tableauCourantRang === 'number' ? salon.tableauCourantRang : null,
        salonRecu: true,
        reservations: new Map(
          (salon.reservations ?? []).map((r) => [r.elementId, r.prenom]),
        ),
      }));
    });

    /**
     * **F-20 — LE BANDEAU D'OBSERVATION, ANNONCÉ PAR LE CANAL ET PAR LUI SEUL.**
     *
     * Comme l'activité, et pour la même raison : l'observation **commence et s'arrête
     * pendant le cours**, et un écran qui aurait demandé l'état au serveur de son côté
     * l'aurait montré à des instants différents chez chacun — ou ne l'aurait jamais
     * retiré.
     *
     * **La charge est vérifiée champ par champ**, leçon de D-110 §1 : « les charges de
     * socket ne sont pas dans le contrat OpenAPI, donc un `as` est une **affirmation**
     * sur une donnée venue du réseau ». Ici l'affirmation fausse laisserait le bandeau
     * posé pour toujours.
     */
    socket.on('observation', (charge: unknown) => {
      if (typeof charge !== 'object' || charge === null) return;
      const observee = (charge as Record<string, unknown>).observee;
      if (typeof observee !== 'boolean') return;
      poserLEtat((precedent) => ({ ...precedent, observee }));
    });

    // ── `onRemoteUpdate` ──
    // « **Tout ce qu'une personne pose part vers tous les autres présents, sans filtre
    // et sans délai voulu. Aucun rôle ne reçoit moins qu'un autre** » (DD-07).
    socket.on('remoteUpdate', (charge: unknown) => {
      const maj = charge as { auteurId: string; contenu: unknown };
      if (maj.contenu === undefined || maj.contenu === null) return;
      // **L'élément porte son identité, et c'est le moteur qui la lui donne** : le canal
      // n'en fabrique plus. C'était nécessaire quand un tracé n'avait pas d'identifiant ;
      // depuis D-115 il en a un, et c'est lui qui permet de reconnaître une MISE À JOUR
      // du même élément — un trait qu'on prolonge, une forme qu'on déplace.
      const element = maj.contenu as ElementDeTableau;
      poserLEtat((precedent) => ({
        ...precedent,
        recus: [...precedent.recus, element],
      }));
    });

    // ── `onRemotePointer` ──
    socket.on('remotePointer', (charge: unknown) => {
      if (!estUnCurseur(charge)) return;
      poserLEtat((precedent) => {
        const curseurs = new Map(precedent.curseurs);
        curseurs.set(charge.auteurId, charge);
        return { ...precedent, curseurs };
      });
    });

    socket.on('reservation', (charge: unknown) => {
      const r = charge as {
        elementId: string;
        issue: string;
        prenom: string | null;
      };
      poserLEtat((precedent) => {
        const reservations = new Map(precedent.reservations);
        if (r.issue === 'libre') reservations.delete(r.elementId);
        else reservations.set(r.elementId, r.prenom);
        return { ...precedent, reservations };
      });
    });

    // ── `publishActivity` ── F-18, D-120.
    // « Elle s'affiche **à tout le monde en même temps** » (Q290) : c'est pour cela
    // qu'elle passe par le canal. Une charge malformée est ignorée plutôt que crue —
    // l'état d'avant est plus juste qu'une fenêtre inventée.
    socket.on('activite', (charge: unknown) => {
      if (!estUneActivite(charge)) return;
      poserLEtat((precedent) => ({ ...precedent, activite: charge }));
    });

    // « Le produit le lui dit — coupure trop longue, tracé non retenu — **et rien n'est
    // proposé pour le récupérer**. »
    socket.on('versionRefusee', () => {
      poserLEtat((precedent) => ({ ...precedent, versionRefusee: true }));
    });

    return () => {
      // ── `disconnect` ──
      clearInterval(battement);
      socket.disconnect();
      socket.close();
      liaison.current = null;
      poserLEtat(ETAT_INITIAL);
    };
  }, [coursId]);

  return {
    etat,
    /** ── `publishUpdate` ── */
    diffuserLElement: (tableauId: string, element: ElementDeTableau) => {
      liaison.current?.emit('publishUpdate', { tableauId, contenu: element });
    },
    /** ── `publishPointer` ── */
    diffuserLePointeur: (
      tableauId: string,
      x: number,
      y: number,
      trace: boolean,
    ) => {
      liaison.current?.emit('publishPointer', { tableauId, x, y, trace });
    },
    attraper: (tableauId: string, elementId: string) => {
      liaison.current?.emit('attraper', { tableauId, elementId });
    },
    relacher: (tableauId: string, elementId: string) => {
      liaison.current?.emit('relacher', { tableauId, elementId });
    },
  };
}
